"use client"

import { useEffect, useRef, useState } from "react"

import { CloudRain, Droplets, Snowflake, Sprout, Sun, Thermometer } from "lucide-react"

import { cn } from "@/lib/utils"
import { trackEvent } from "@/lib/analytics"
import { fetchWeather, type WeatherData } from "@/lib/weather-utils"
import { buildWeatherWeek, type WeatherWeek, type WeatherWeekKind, type WeatherWeekLead } from "@/lib/weather-week"

/**
 * The week ahead at the user's ZIP, on the grass-ID result card: one lead line
 * that says what the week means, over a seven-day rainfall/high strip that
 * shows the evidence for it.
 *
 * Replaces two generic promises ("12-month schedule tuned to your region",
 * "reminders timed to your season") with the data those promises were about.
 *
 * HEIGHT IS RESERVED FROM FIRST PAINT. Weather is fetched client-side, and this
 * block sits directly above the one free task; if it grew in on arrival it would
 * push that task down while the user was reading it. The skeleton occupies the
 * loaded layout exactly, so nothing below ever moves.
 */

/** How long to wait for the experiment flag before tracking without it. */
const FLAG_SETTLE_TIMEOUT_MS = 4000

const KIND_STYLES: Record<WeatherWeekKind, { wrap: string; icon: string }> = {
  rain: { wrap: "border-blue-100 bg-blue-50/70", icon: "text-blue-700" },
  frost: { wrap: "border-sky-100 bg-sky-50/70", icon: "text-sky-700" },
  heat: { wrap: "border-amber-100 bg-amber-50/70", icon: "text-amber-700" },
  dry: { wrap: "border-green-100 bg-green-50/60", icon: "text-green-700" },
  // Earth tones, deliberately outside the four weather hues: the lead is about
  // the ground, not the sky.
  soil: { wrap: "border-[#e2d6c3] bg-[#f6f0e6]", icon: "text-[#7a5a34]" },
}

function LeadIcon({ lead, className }: { lead: WeatherWeekLead; className?: string }) {
  const props = { className, strokeWidth: 2, "aria-hidden": true as const }
  const { kind } = lead
  if (kind === "rain") return <CloudRain {...props} />
  if (kind === "frost") return <Snowflake {...props} />
  if (kind === "heat") return <Sun {...props} />
  // Soil keeps one colour family; the icon carries the decision. A go verdict
  // gets a green sprout, a hold verdict the umber thermometer, so which side
  // of the line you are on reads before the words do.
  if (kind === "soil") {
    return lead.stance === "go" ? (
      <Sprout {...props} className={cn(className, "text-green-700")} />
    ) : (
      <Thermometer {...props} />
    )
  }
  return <Droplets {...props} />
}

/**
 * The headline with its figures set heavier than its words, so the eye lands
 * on 58° → 65° (or 0.5″) before it reads the sentence around them.
 */
function Headline({ text }: { text: string }) {
  const parts = text.split(/(\d+(?:\.\d+)?[°″]?)/)
  return (
    <div className="text-[13.5px] font-semibold leading-tight tracking-[-0.01em] text-gray-900">
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className="font-extrabold tabular-nums">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </div>
  )
}

/** Plain-language summary of the whole chart for screen readers, which get no
 *  value from seven unlabelled bars. */
function weekAriaLabel(week: WeatherWeek): string {
  const days = week.days
    .map((day) => {
      const high = day.highF !== null ? `${day.highF} degrees` : "high unavailable"
      // A day can be a rain day on chance alone, with no forecast amount and so
      // no bar on screen. Reporting only inches would drop it entirely here.
      const rain =
        day.precipIn > 0
          ? `, ${day.precipIn.toFixed(2)} inches of rain`
          : day.isRain
            ? `, ${day.precipChance} percent chance of rain`
            : ""
      return `${day.isToday ? "Today" : day.shortLabel}: ${high}${rain}`
    })
    .join(". ")
  return `Seven day forecast. ${days}.`
}

/** One line per fact, so the bubble reads as a mini forecast card. */
function dayTooltipLines(day: WeatherWeek["days"][number]): string[] {
  const lines: string[] = []
  const high = day.highF !== null ? `${day.highF}°` : "—"
  lines.push(day.lowF !== null ? `${high} high · ${day.lowF}° low` : `${high} high`)
  if (day.precipIn > 0) {
    lines.push(`${day.precipIn.toFixed(2)}″ rain expected`)
  } else if (day.isRain) {
    lines.push(`${day.precipChance}% chance of rain`)
  } else {
    lines.push("No rain expected")
  }
  return lines
}

function DayColumn({ day }: { day: WeatherWeek["days"][number] }) {
  return (
    <div className="group relative flex min-w-0 flex-col items-center gap-1">
      {/* Hover-only detail bubble. Pure CSS so seven of these cost nothing;
          pointer-events-none keeps it from trapping the cursor mid-scan.
          Hidden from screen readers — the strip's aria-label already narrates
          every day, and no tab stop is added for content assistive tech
          cannot reach. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2.5 py-1.5 text-left opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.05em] text-gray-400">
          {day.isToday ? "Today" : day.shortLabel}
        </p>
        {dayTooltipLines(day).map((line) => (
          <p key={line} className="text-[11.5px] font-semibold leading-snug text-white tabular-nums">
            {line}
          </p>
        ))}
      </div>

      <span
        className={cn(
          "text-[9.5px] font-bold uppercase tracking-[0.04em]",
          day.isToday ? "text-green-600" : "text-gray-400",
        )}
      >
        {/* Narrow columns (~34px) take the initial; the green is what marks
            today. A bullet here would break the weekday run the reader scans. */}
        <span className="md:hidden">{day.initial}</span>
        <span className="hidden md:inline">{day.shortLabel}</span>
      </span>

      <span className="flex h-[34px] w-full items-end overflow-hidden rounded bg-gray-100">
        {day.barPct > 0 ? (
          <span className="w-full rounded bg-blue-400" style={{ height: `${day.barPct}%` }} />
        ) : (
          // A measured zero, not missing data. The 3px tick keeps a dry week
          // from reading as seven broken tiles.
          <span className="h-[3px] w-full rounded bg-gray-300" />
        )}
      </span>

      <span
        className={cn(
          "text-[11px] font-bold tabular-nums leading-none",
          day.isHot ? "text-amber-600" : "text-gray-900",
        )}
      >
        {day.highF !== null ? `${day.highF}°` : "—"}
      </span>
      {/* Heat days carry a second cue besides colour, so the pattern survives
          for users who can't separate amber from near-black. */}
      <span
        className={cn("h-[2px] w-3 rounded", day.isHot ? "bg-amber-400" : "bg-transparent")}
        aria-hidden="true"
      />
      <span className="hidden text-[9.5px] tabular-nums leading-none text-gray-400 md:block">
        {day.lowF !== null ? `${day.lowF}°` : ""}
      </span>
    </div>
  )
}

function WeekSkeleton() {
  return (
    <div aria-hidden="true" className="animate-pulse">
      <div className="flex min-h-[54px] items-center gap-2.5 rounded-xl border border-gray-100 bg-gray-50/70 px-3 py-2.5">
        <span className="h-[18px] w-[18px] flex-shrink-0 rounded-full bg-gray-200" />
        <span className="min-w-0 flex-1">
          <span className="block h-[13px] w-2/3 rounded bg-gray-200" />
          <span className="mt-1.5 block h-[10px] w-1/2 rounded bg-gray-100" />
        </span>
      </div>
      <div className="mt-2.5 grid grid-cols-7 gap-1">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex min-w-0 flex-col items-center gap-1">
            <span className="h-[9.5px] w-3 rounded bg-gray-100" />
            <span className="h-[34px] w-full rounded bg-gray-100" />
            <span className="h-[11px] w-5 rounded bg-gray-100" />
            <span className="h-[2px] w-3" />
            <span className="hidden h-[9.5px] w-4 rounded bg-gray-100 md:block" />
          </div>
        ))}
      </div>
      <div className="mt-1.5 h-[10.5px] w-full rounded bg-gray-100" />
    </div>
  )
}

interface ResultWeekStripProps {
  zipCode: string
  /**
   * This month's task prose. Used only to decide whether the lead line may
   * claim an action: we never say "water Friday instead" when the month's plan
   * contains no watering task.
   */
  taskTexts: string[]
  /** Identified grass, for the seeding soil verdict. */
  grassType?: string
  /** Whether that name is settled; the seeding verdict is withheld otherwise. */
  grassConfirmed?: boolean
  /**
   * Experiment arm. True renders the soil verdict/footer token; anything else
   * is today's four-kind strip. Read in render, not the fetch effect, so a flag
   * that resolves after the forecast lands flips the lead without a refetch.
   */
  soil?: boolean
  /**
   * Whether the experiment flag has resolved. Until it has, `soil` is only a
   * default, so the analytics event holds: an exposure stamped "control" for a
   * person who is really in the soil arm would contaminate the control bucket.
   * Rendering is not held — the strip shows control until the flag lands and
   * flips in place. Defaults to true for callers with no experiment.
   */
  flagSettled?: boolean
  className?: string
}

interface StripState {
  /** The ZIP this result describes, so a changed ZIP can't render last ZIP's week. */
  zip: string
  status: "loading" | "ready" | "error"
  data: WeatherData | null
}

export function ResultWeekStrip({
  zipCode,
  taskTexts,
  grassType,
  grassConfirmed = false,
  soil = false,
  flagSettled = true,
  className,
}: ResultWeekStripProps) {
  // taskTexts is a fresh array identity every render; its CONTENT is what
  // changes the lead action (the user can switch to an alternative grass, which
  // swaps the whole month's task list).
  // Held in refs, NOT dependencies: the caller builds the task array inline, so
  // its identity changes every render and depending on it would refetch in a
  // loop; the soil arm resolves whenever PostHog answers. Both are read at
  // resolve time for the analytics event and again in render for the chart.
  // (Same treatment as useImpressionEvent's propsRef, for the same reason.)
  const contextRef = useRef({ taskTexts, soil, grassType, grassConfirmed })
  contextRef.current = { taskTexts, soil, grassType, grassConfirmed }

  const [state, setState] = useState<StripState>({
    zip: zipCode,
    status: "loading",
    data: null,
  })

  useEffect(() => {
    let cancelled = false

    fetchWeather(zipCode)
      .then((data) => {
        if (cancelled) return
        const { taskTexts: tasks, ...ctx } = contextRef.current
        const built = buildWeatherWeek(data, tasks, ctx)
        if (!built) {
          setState({ zip: zipCode, status: "error", data: null })
          trackEvent("result_week_strip_resolved", { status: "empty", zip_prefix: zipCode.slice(0, 3) })
          return
        }
        setState({ zip: zipCode, status: "ready", data })
      })
      .catch(() => {
        if (cancelled) return
        setState({ zip: zipCode, status: "error", data: null })
        trackEvent("result_week_strip_resolved", { status: "error", zip_prefix: zipCode.slice(0, 3) })
      })

    return () => {
      cancelled = true
    }
  }, [zipCode])

  // Derived in render, not cleared in an effect: the effect runs AFTER paint, so
  // clearing there would still flash the previous ZIP's forecast under the new
  // ZIP's footer for a frame. The week is rebuilt from the stored payload here,
  // so a task-list swap (alternative grass) or a late-resolving flag re-reads
  // the same forecast instead of refetching it.
  const current = state.zip === zipCode ? state : null
  const status = current?.status ?? "loading"
  const week = current?.data
    ? buildWeatherWeek(current.data, taskTexts, { soil, grassType, grassConfirmed })
    : null

  // The "ready" event describes what was RENDERED, so it fires from the
  // rendered week rather than from the fetch: a flag that resolves after the
  // forecast flips the lead without a refetch, and the arm read off
  // `soil_variant` has to follow it. One event per distinct (zip, kind, arm);
  // a flip is a second event with `resolution: "flip"`.
  // If flags never resolve (PostHog blocked or off), the event still has to
  // fire or the surface goes dark in analytics; after this long it fires with
  // `flag_settled: false` so those records can be excluded from an arm read.
  const [settleTimedOut, setSettleTimedOut] = useState(false)
  useEffect(() => {
    if (flagSettled) return
    const timer = setTimeout(() => setSettleTimedOut(true), FLAG_SETTLE_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [flagSettled])
  const canTrack = flagSettled || settleTimedOut

  const trackedRef = useRef<string | null>(null)
  const leadKind = week?.lead.kind ?? null
  const soilVariant = soil ? "soil" : "control"
  useEffect(() => {
    if (!week || leadKind === null || !canTrack) return
    const key = `${zipCode}|${leadKind}|${soilVariant}`
    if (trackedRef.current === key) return
    const resolution = trackedRef.current?.startsWith(`${zipCode}|`) ? "flip" : "initial"
    trackedRef.current = key
    trackEvent("result_week_strip_resolved", {
      status: "ready",
      resolution,
      kind: leadKind,
      rain_days: week.rainDays,
      has_action: week.lead.action !== null,
      soil_variant: soilVariant,
      flag_settled: flagSettled,
      soil_temp_f: week.soilTempF,
      zip_prefix: zipCode.slice(0, 3),
    })
    // `week` is rebuilt every render; the key captures what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCode, leadKind, soilVariant, canTrack])

  if (status === "error") {
    return <ResultWeekStripFallback zipCode={zipCode} className={className} />
  }

  if (status === "loading") {
    return (
      <div className={className}>
        <WeekSkeleton />
      </div>
    )
  }

  if (!week) {
    return <ResultWeekStripFallback zipCode={zipCode} className={className} />
  }

  // The forecast arrives after paint, so it fades in over the skeleton it
  // replaces rather than popping; the height is already reserved, so nothing
  // else moves. Off under prefers-reduced-motion.
  return (
    <WeekStripView
      week={week}
      locationLabel={`ZIP ${zipCode}`}
      className={cn(className, "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200")}
    />
  )
}

/**
 * The chart itself, with no opinion about where the week came from.
 *
 * Split out so the saved-result page can render a week its SERVER built. That
 * page holds a ZIP it must not send to a bearer-link viewer, so it derives the
 * forecast server-side and passes the numbers plus a region label down. Same
 * pixels either way — the only difference is what names the location.
 */
export function WeekStripView({
  week,
  locationLabel,
  className,
}: {
  week: WeatherWeek
  /** Footer location, e.g. "ZIP 53528" or "the Upper Midwest". */
  locationLabel: string
  className?: string
}) {
  const styles = KIND_STYLES[week.lead.kind]
  // Soil rides the footer only when it did NOT take the lead: the number is
  // already in the headline then, and stating it twice on a 286px card reads
  // as padding. Null on the control arm.
  const showSoilToken = week.soilTempF !== null && week.lead.kind !== "soil"
  // Same rule for the peak high: a heat lead already headlines it.
  const showPeakToken =
    week.peakHighF !== null && week.peakHighLabel !== null && week.lead.kind !== "heat"

  return (
    <div className={className}>
      {/* min-h holds the two-line box even when the action line is suppressed.
          The action is null whenever the month's tasks can't back the claim, so
          without this the block resolves SHORTER than its own skeleton and the
          free task jumps up — the same shift the skeleton exists to prevent,
          just in the other direction. */}
      <div
        className={cn(
          "flex min-h-[54px] items-center gap-2.5 rounded-xl border px-3 py-2.5",
          styles.wrap,
        )}
      >
        <LeadIcon lead={week.lead} className={cn("h-[18px] w-[18px] flex-shrink-0", styles.icon)} />
        <div className="min-w-0">
          <Headline text={week.lead.headline} />
          {week.lead.action && (
            <div className="mt-0.5 text-[11px] leading-snug text-gray-600">{week.lead.action}</div>
          )}
        </div>
      </div>

      <div className="mt-2.5 grid grid-cols-7 gap-1" role="img" aria-label={weekAriaLabel(week)}>
        {week.days.map((day) => (
          <DayColumn key={day.date} day={day} />
        ))}
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-2 text-[10.5px] text-gray-500">
        {/* On a dry week every bar is an empty track, which reads as a broken
            chart rather than a measured zero. Name it here so the flat row is
            the answer instead of a missing one. */}
        {/* With a soil token on the right the descriptor no longer fits at
            286px and truncated to "ZIP…", so the label drops to the location
            alone; the bars and highs explain themselves. */}
        <span className="truncate">
          {showSoilToken
            ? locationLabel
            : week.rainDays === 0 && week.totalPrecipIn < 0.01
              ? `No rain forecast · ${locationLabel}`
              : `Rainfall and highs · ${locationLabel}`}
        </span>
        <span className="flex flex-shrink-0 items-center gap-1.5">
          {showSoilToken && (
            <span>
              Soil <span className="font-bold text-gray-900 tabular-nums">{week.soilTempF}°</span>
            </span>
          )}
          {showSoilToken && showPeakToken && (
            <span aria-hidden="true" className="text-gray-300">·</span>
          )}
          {showPeakToken && (
            <span>
              Peak <span className="font-bold text-gray-900 tabular-nums">{week.peakHighF}°</span>{" "}
              {week.peakHighLabel}
            </span>
          )}
        </span>
      </div>
    </div>
  )
}

/**
 * The two checklist lines this strip replaces. Rendered when the flag is off,
 * when there's no ZIP, or when the forecast can't be fetched — so a weather
 * outage degrades to the previous design rather than to a hole in the card.
 */
export function ResultWeekStripFallback({
  zipCode,
  className,
}: {
  zipCode?: string
  className?: string
}) {
  return (
    <ul className={cn("space-y-2", className)}>
      <li className="flex items-start gap-2">
        <CheckDot />
        <span className="text-sm leading-snug text-gray-700">
          12-month schedule tuned to {zipCode ? `ZIP ${zipCode}` : "your region"}
        </span>
      </li>
      <li className="flex items-start gap-2">
        <CheckDot />
        <span className="text-sm leading-snug text-gray-700">Reminders timed to your season</span>
      </li>
    </ul>
  )
}

function CheckDot() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m8 12 3 3 5-6" />
    </svg>
  )
}
